<template>
  <MarketplaceCard
    v-if="jbWarranties.config"
    v-model="switchOn"
    :disabled="switchDisabled"
    :logo-path="imageUrl"
    logo-alt="JB Warranties"
    description="Our online system provides contractors with the most comprehensive and easy to use extended service agreement solution."
    learn-more-link="https://www.jbwarranties.com/"
    cy-selector="marketplaceJBW"
  >
    <template #configure>
      <div class="link" @click="openModal">Set Up Information</div>
    </template>
    <template #modal>
      <Modal
        v-if="modalOpen"
        title="Set Up Information - JB Warranties"
        class-name="jb-warranties-config-modal"
        no-submit
        @hide-modal="closeModal"
      >
        <div class="d-flex flex-column">
          <ActionInput
            v-model="jbWarranties.config.api_key"
            label="API Key"
            placeholder="Enter API Key"
            button-text="Validate"
            input-id="jbw-api-key"
            :loading="validating"
            :validated="validated"
            :masked="true"
            :locked="validated"
            class="mb-3"
            @action="validateApiKey"
            @unlock="unlockApiKey"
          />
          <div v-if="jbWarranties.config.verified_at" class="mt-3">
            <hr class="border-gray-200" />
            <span class="block mb-2 mt-3 text-sm font-medium text-gray-900">
              Dealer Information (Populated from API)
            </span>
            <div class="d-flex gap-3 mb-2">
              <InputField
                :model-value="jbWarranties.config.contact_first_name || ''"
                class="flex-1"
                title="First Name"
                :disabled="true"
              />
              <InputField
                :model-value="jbWarranties.config.contact_last_name || ''"
                class="flex-1"
                title="Last Name"
                :disabled="true"
              />
            </div>
            <div class="d-flex gap-3 mb-2">
              <InputField
                :model-value="jbWarranties.config.ein || ''"
                class="flex-1"
                title="EIN"
                :disabled="true"
              />
              <InputField
                :model-value="jbWarranties.config.state_license_number || ''"
                class="flex-1"
                title="State License Number"
                :disabled="true"
              />
              <InputField
                :model-value="jbWarranties.config.dealer_id?.toString() || ''"
                class="flex-1"
                title="Dealer ID"
                :disabled="true"
              />
            </div>
          </div>
        </div>
        <template #footer>
          <div
            class="modal-footer d-flex justify-content-between align-items-center"
          >
            <div class="d-flex gap-2">
              <Button v-if="unsaved" type="secondary" @click="closeModal()">
                Cancel
              </Button>
              <Button
                v-if="jbWarranties.config.persisted"
                type="danger"
                @click="confirmDelete()"
              >
                Delete
              </Button>
            </div>
            <Alert
              v-if="validatedUnsaved && !saving"
              text="API Key validated, but not yet saved."
              type="warning"
              class="!py-2.5"
            />
            <Button :disabled="!dirty || saving" @click="saveIntegration">
              {{ saving ? "Saving..." : "Save" }}
            </Button>
          </div>
        </template>
      </Modal>
      <Modal
        v-if="deleteConfirm"
        title="Delete Configuration"
        @hide-modal="toggleDeleteConfirm"
      >
        <div>
          This configuration will be permanently deleted and you will not be
          able to undo this action. Are you sure?
        </div>
        <template #submit>
          <Button id="submit_modal_btn" type="danger" @click="dropIntegration">
            Delete Configuration
          </Button>
        </template>
      </Modal>
    </template>
  </MarketplaceCard>
</template>

<script>
import { mapActions, mapState } from "vuex";

import Button from "@/components/Button.vue";
import Modal from "@/components/Modal.vue";
import ActionInput from "@/components/Seranova/ActionInput/ActionInput.vue";
import Alert from "@/components/Seranova/Alert/Alert.vue";
import InputField from "@/components/Seranova/InputField/InputField.vue";
import JBWarrantiesLogo from "@/images/jb_warranties_logo.png";
import MarketplaceCard from "./MarketplaceCard.vue";

export default {
  components: {
    MarketplaceCard,
    Modal,
    ActionInput,
    Alert,
    InputField,
    Button
  },
  data() {
    return {
      deleteConfirm: false,
      switchDisabled: null,
      switchOn: null,
      modalDropVisible: null,
      modalOpen: false,
      saving: false,
      validating: false,
      dirty: false
    };
  },
  computed: {
    ...mapState(["jbWarranties"]),
    imageUrl: {
      get() {
        return JBWarrantiesLogo;
      }
    },
    validated() {
      return !!this.jbWarranties.config.verified_at;
    },
    unsaved() {
      return !this.jbWarranties.config.persisted || this.dirty;
    },
    validatedUnsaved() {
      return this.validated && this.unsaved;
    }
  },
  watch: {
    switchOn: function (newValue, oldValue) {
      if (
        oldValue === null ||
        newValue === oldValue ||
        newValue === this.jbWarranties.config.enabled
      ) {
        return;
      }
      newValue ? this.enableIntegration() : this.disableIntegration();
    },
    "jbWarranties.config.enabled": function (newValue, oldValue) {
      if (oldValue !== newValue) {
        this.refreshTools();
      }
    },
    "jbWarranties.config.api_key": function (newValue, oldValue) {
      if (oldValue !== null && newValue === null) {
        this.refreshModal();
      }
      // Clear validation when the key is changed after a successful validate
      if (
        oldValue !== null &&
        oldValue !== newValue &&
        this.jbWarranties.config.verified_at
      ) {
        this.jbWarranties.config.contact_first_name = null;
        this.jbWarranties.config.contact_last_name = null;
        this.jbWarranties.config.dealer_id = null;
        this.jbWarranties.config.ein = null;
        this.jbWarranties.config.state_license_number = null;
        this.jbWarranties.config.verified_at = null;
      }
    }
  },
  mounted() {
    this.fetchJBWarranties().then(this.refreshTools);
  },
  methods: {
    ...mapActions([
      "fetchJBWarranties",
      "changeJBWarranties",
      "dropJBWarranties",
      "validateJBWarranties"
    ]),
    openModal() {
      this.fetchJBWarranties().then(() => {
        this.dirty = false;
        this.modalOpen = true;
        this.refreshModal();
      });
    },
    closeModal() {
      return this.fetchJBWarranties().then(() => {
        this.modalOpen = false;
        this.refreshTools();
      });
    },
    enableIntegration() {
      this.changeJBWarranties({ enabled: true })
        .then(() => {
          window.notify.success("JB Warranties integration enabled!");
        })
        .catch(() => (this.switchOn = false));
    },
    disableIntegration() {
      this.changeJBWarranties({ enabled: false }).then(() => {
        window.notify.success("JB Warranties integration disabled!");
      });
    },
    async validateApiKey() {
      try {
        this.validating = true;
        const result = await this.validateJBWarranties({
          api_key: this.jbWarranties.config.api_key
        });

        // Update the config with the validated data
        Object.assign(this.jbWarranties.config, result.config);
        this.dirty = true;
        window.notify.success("API Key validated successfully!");
      } catch (e) {
        // Clear the read-only fields when validation fails
        this.jbWarranties.config.contact_first_name = null;
        this.jbWarranties.config.contact_last_name = null;
        this.jbWarranties.config.dealer_id = null;
        this.jbWarranties.config.ein = null;
        this.jbWarranties.config.state_license_number = null;
        this.jbWarranties.config.verified_at = null;
        // form shows validation errors
      }
      this.validating = false;
    },
    async saveIntegration() {
      try {
        this.saving = true;
        await this.changeJBWarranties(this.jbWarranties.config);
        this.dirty = false;
        window.notify.success("JB Warranties settings saved!");
        this.closeModal();
      } catch (e) {
        // form shows validation errors
      }
      this.saving = false;
    },
    dropIntegration() {
      this.deleteConfirm = false;
      this.closeModal().then(() => {
        this.switchOn = false;
        if (this.jbWarranties.config.persisted) {
          this.dropJBWarranties().then(() => {
            window.notify.success("JB Warranties settings deleted!");
          });
        } else {
          window.notify.error(
            "The integration has not been configured yet. There's nothing to delete."
          );
        }
      });
    },
    // Make the state consistent
    refreshTools() {
      this.switchOn = this.jbWarranties.config.enabled;
      if (this.switchDisabled) {
        this.switchOn = false;
      }
    },
    refreshModal() {
      if (!this.modalOpen) {
        return;
      }

      // remove blank values
      if (this.jbWarranties.config.api_key === "") {
        this.jbWarranties.config.api_key = null;
      }
    },
    unlockApiKey() {
      this.jbWarranties.config.contact_first_name = null;
      this.jbWarranties.config.contact_last_name = null;
      this.jbWarranties.config.dealer_id = null;
      this.jbWarranties.config.ein = null;
      this.jbWarranties.config.state_license_number = null;
      this.jbWarranties.config.verified_at = null;
      this.dirty = false;
    },
    confirmDelete() {
      this.deleteConfirm = true;
    },
    toggleDeleteConfirm() {
      this.deleteConfirm = !this.deleteConfirm;
    }
  }
};
</script>
